import { PlatformOSType } from 'react-native';
import Service from 'react-reservice';

import {
  EFeatureListType,
  ELoginConfigType,
  EMainProductCodeType,
  EMarketTimeManagementType,
  ESubProductCodeType,
  ICompany,
  companyAPI,
  companyUserAPI
} from '@webtrader/core/api/config/company';
import contactAPI, { ICompanyContact } from '@webtrader/core/api/config/contact';
import { ELoginAs } from '@webtrader/core/constants/account';
import dayjs, { IGetUTCOffsetDateTimeAsDayjsParams, getUTCOffsetDateTimeAsDayjs } from '@webtrader/core/dayjs';
import i18n, { initialLanguageChange } from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { themeService } from '@webtrader/core/services/theme/v2';
import storage, { EStorageKey } from '@webtrader/core/storage';

import debugLogService from '../debug-log';

interface IConvertToDayjsWithOffsetParams
  extends Pick<IGetUTCOffsetDateTimeAsDayjsParams, 'input' | 'keepTime' | 'baseFromOffset'> {}

export interface ICompanyAccess {
  news: boolean;
  margin: boolean;
  signal: boolean;
  calendar: boolean;
  copyTrade: boolean;
  resetPassword: boolean;
  changePassword: boolean;
  customerService: boolean;
  topGainer: boolean;
  topLoser: boolean;
  topPick: boolean;
  portfolioPieChart: boolean;
  fundManagement: boolean;
  companyLoginConfig: boolean;
  mt4LiveLoginConfig: boolean;
  mt4DemoLoginConfig: boolean;
  mt5LiveLoginConfig: boolean;
  mt5DemoLoginConfig: boolean;
}

type IRegistrationDevices = Partial<Record<PlatformOSType, string | undefined>>;

const defaultCompanyAccess: ICompanyAccess = {
  news: false,
  margin: false,
  signal: false,
  calendar: false,
  copyTrade: false,
  resetPassword: false,
  changePassword: false,
  customerService: false,
  topGainer: false,
  topLoser: false,
  topPick: false,
  portfolioPieChart: false,
  fundManagement: false,
  companyLoginConfig: false,
  mt4LiveLoginConfig: false,
  mt4DemoLoginConfig: false,
  mt5LiveLoginConfig: false,
  mt5DemoLoginConfig: false
};

export interface ICompanyContext {
  company?: ICompany;
  contact?: ICompanyContact;
  langInitLoading?: boolean;
  loading?: boolean;
  access: ICompanyAccess;
  mainProductCode?: EMainProductCodeType;
  errorMessage?: string;
}

const defaultContext: ICompanyContext = {
  langInitLoading: true,
  loading: true,
  errorMessage: undefined,
  access: { ...defaultCompanyAccess },
  mainProductCode: undefined
};

class CompanyService extends Service<ICompanyContext> {
  private companyAPICalling = false;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = (backoffice?: boolean) => {
    return this.retrieveCompanyProfile(backoffice).then((company) => {
      if (backoffice) {
        return;
      }
      const { defaultLanguage } = company || {};
      initialLanguageChange(defaultLanguage, async () => {
        this.context.langInitLoading = false;
      });
    });
  };

  private callCompanyUserAPI = async () => {
    try {
      return await companyUserAPI();
    } catch {
      return await companyAPI();
    }
  };

  private checkCompanyApi = async (backoffice?: boolean) => {
    const token = storage.getItem(EStorageKey.TOKEN);
    const { result } = token && !backoffice ? await this.callCompanyUserAPI() : await companyAPI();
    return result;
  };

  public retrieveCompanyProfile = async (backoffice?: boolean) => {
    if (!this.companyAPICalling) {
      try {
        this.companyAPICalling = true;
        this.context.loading = this.context.company === undefined;
        this.context.errorMessage = undefined;

        const result = await this.checkCompanyApi(backoffice);

        if (result) {
          const { profitColourCode, lossColourCode } = result;
          themeService.setThemeByTenant(result.tenantId, backoffice);
          themeService.setPrimaryColor(result.themeColourCode);
          themeService.setDefaultCandlestickColors({
            positive: profitColourCode,
            negative: lossColourCode
          });
          // @TODO - To remove the centroid check
          const isCentroid = result?.tenantId === 'centroid';
          this.context.company = {
            ...result,
            profitColourCode,
            lossColourCode,
            mainProductCodeList: isCentroid
              ? [EMainProductCodeType.CENTROID, ...result.mainProductCodeList]
              : result.mainProductCodeList,
            currentMainProductCode: isCentroid ? EMainProductCodeType.CENTROID : result.currentMainProductCode
          };
          const mainProductCode = result?.currentMainProductCode || result?.mainProductCodeList[0];
          this.context.mainProductCode = isCentroid ? EMainProductCodeType.CENTROID : mainProductCode;
          this.populateCompanyAccess();
          debugLogService.log('Company Profile', 'success');
        }
      } catch (err) {
        const error = err as IApiError;
        const { code, message } = error;
        this.context.errorMessage = mapResponseCodeToMessage(code, message);
        debugLogService.log('Company Profile', 'error', error);
      } finally {
        this.context.loading = false;
        this.companyAPICalling = false;
      }
    }

    return this.context.company;
  };

  private populateCompanyAccess = () => {
    const companyAccess = { ...defaultCompanyAccess };
    const company = this.context.company;
    if (company) {
      this.massageCompanyAccess(company, companyAccess);
    }

    this.context.access = companyAccess;
  };

  // eslint-disable-next-line complexity
  public massageCompanyAccess = (company: ICompany, companyAccess: ICompanyAccess) => {
    if (company) {
      companyAccess.fundManagement = company.fundManagement;

      for (const code of company.subProductCodeList) {
        switch (code) {
          case ESubProductCodeType.NEWS:
            companyAccess.news = true;
            break;
          case ESubProductCodeType.SIGNAL:
            companyAccess.news = true;
            break;
          case ESubProductCodeType.CALENDAR:
            companyAccess.calendar = true;
            break;
          case ESubProductCodeType.COPY_TRADE:
            companyAccess.copyTrade = true;
            break;
        }
      }

      for (const code of company.mainProductCodeList) {
        switch (code) {
          case EMainProductCodeType.METATRADER4:
          case EMainProductCodeType.METATRADER5:
            companyAccess.margin = true;
            break;
        }
      }

      for (const code of company.featureList) {
        switch (code) {
          case EFeatureListType.RESET_PASSWORD:
            companyAccess.resetPassword = true;
            break;
          case EFeatureListType.CHANGE_PASSWORD:
            companyAccess.changePassword = true;
            break;
          case EFeatureListType.CUSTOMER_SERVICE:
            companyAccess.customerService = true;
            break;
          case EFeatureListType.TOP_GAINER:
            companyAccess.topGainer = true;
            break;
          case EFeatureListType.TOP_LOSER:
            companyAccess.topLoser = true;
            break;
          case EFeatureListType.TOP_PICK:
            companyAccess.topPick = true;
            break;
          case EFeatureListType.PIE_CHART:
            companyAccess.portfolioPieChart = true;
            break;
        }
      }

      for (const code of company.loginConfigList) {
        switch (code) {
          case ELoginConfigType.COMPANY:
            companyAccess.companyLoginConfig = true;
            break;
          case ELoginConfigType.METATRADER4_LIVE:
            companyAccess.mt4LiveLoginConfig = true;
            break;
          case ELoginConfigType.METATRADER4_DEMO:
            companyAccess.mt4DemoLoginConfig = true;
            break;
          case ELoginConfigType.METATRADER5_LIVE:
            companyAccess.mt5LiveLoginConfig = true;
            break;
          case ELoginConfigType.METATRADER5_DEMO:
            companyAccess.mt5DemoLoginConfig = true;
            break;
        }
      }
    }
  };

  public getIntegration = () => {
    const { mainProductCodeList = [], currentMainProductCode, tenantId } = this.context.company || {};
    // @TODO - Remove check in future
    if (tenantId === 'centroid') {
      return EMainProductCodeType.CENTROID;
    }
    return currentMainProductCode ?? mainProductCodeList[0];
  };

  public fetchCompanyContact = async () => {
    try {
      const { result } = await contactAPI();
      this.context.contact = result || undefined;
    } catch {}
  };

  public getRegistrationUrl = (selectedTab: ELoginAs, device: PlatformOSType) => {
    const company = this.context.company;

    if (selectedTab === ELoginAs.LIVE) {
      return company?.companyRegistrationUrl;
    }

    const registrationUrl: Partial<Record<ELoginAs, IRegistrationDevices>> = {
      ...this.getDemoRegistrationUrlObject(),
      ...this.getLiveRegistrationUrlObject()
    };

    return registrationUrl?.[selectedTab]?.[device];
  };

  private get isMT5(): boolean {
    return this.getIntegration() === EMainProductCodeType.METATRADER5;
  }

  private getDemoRegistrationUrlObject = (): Partial<Record<ELoginAs, IRegistrationDevices>> => {
    const { company } = this.context;
    const {
      mt5DemoRegistrationUrl: mt5DemoRegisterUrlWeb,
      mt5DemoRegistrationIosUrl: mt5DemoRegisterUrlIos,
      mt5DemoRegistrationAndroidUrl: mt5DemoRegisterUrlAndroid,

      mt4DemoRegistrationUrl: mt4DemoRegisterUrlWeb,
      mt4DemoRegistrationIosUrl: mt4DemoRegisterUrlIos,
      mt4DemoRegistrationAndroidUrl: mt4DemoRegisterUrlAndroid
    } = company ?? {};

    return {
      [ELoginAs.MT5_DEMO]: {
        web: mt5DemoRegisterUrlWeb,
        ios: mt5DemoRegisterUrlIos,
        android: mt5DemoRegisterUrlAndroid
      },
      [ELoginAs.MT4_DEMO]: {
        web: mt4DemoRegisterUrlWeb,
        ios: mt4DemoRegisterUrlIos,
        android: mt4DemoRegisterUrlAndroid
      },
      [ELoginAs.OMS_DEMO]: {
        web: mt4DemoRegisterUrlWeb ?? mt5DemoRegisterUrlWeb,
        ios: mt4DemoRegisterUrlIos ?? mt5DemoRegisterUrlIos,
        android: mt4DemoRegisterUrlAndroid ?? mt5DemoRegisterUrlAndroid
      }
    };
  };

  public getDemoRegistrationUrl = (device: PlatformOSType) => {
    const loginAs = this.isMT5 ? ELoginAs.MT5_DEMO : ELoginAs.MT4_DEMO;

    return this.getDemoRegistrationUrlObject()[loginAs]?.[device];
  };

  private getLiveRegistrationUrlObject = (): Partial<Record<ELoginAs, IRegistrationDevices>> => {
    const { company } = this.context;
    const {
      mt5LiveRegistrationUrl: mt5LiveRegisterUrlWeb,
      mt5LiveRegistrationIosUrl: mt5LiveRegisterUrlIos,
      mt5LiveRegistrationAndroidUrl: mt5LiveRegisterUrlAndroid,

      mt4LiveRegistrationUrl: mt4LiveRegisterUrlWeb,
      mt4LiveRegistrationIosUrl: mt4LiveRegisterUrlIos,
      mt4LiveRegistrationAndroidUrl: mt4LiveRegisterUrlAndroid
    } = company ?? {};

    return {
      [ELoginAs.MT5_LIVE]: {
        web: mt5LiveRegisterUrlWeb,
        ios: mt5LiveRegisterUrlIos,
        android: mt5LiveRegisterUrlAndroid
      },
      [ELoginAs.MT4_LIVE]: {
        web: mt4LiveRegisterUrlWeb,
        ios: mt4LiveRegisterUrlIos,
        android: mt4LiveRegisterUrlAndroid
      },
      [ELoginAs.OMS_LIVE]: {
        web: mt4LiveRegisterUrlWeb ?? mt5LiveRegisterUrlWeb,
        ios: mt4LiveRegisterUrlIos ?? mt5LiveRegisterUrlIos,
        android: mt4LiveRegisterUrlAndroid ?? mt5LiveRegisterUrlAndroid
      }
    };
  };

  public getLiveRegistrationUrl = (device: PlatformOSType) => {
    const loginAs = this.isMT5 ? ELoginAs.MT5_LIVE : ELoginAs.MT4_LIVE;

    return this.getLiveRegistrationUrlObject()[loginAs]?.[device];
  };

  public getMarketTimeIsServer = () => {
    const { marketTimeManagement } = this.context.company || {};
    return marketTimeManagement === EMarketTimeManagementType.SERVER;
  };

  public getDemoAccess = () => {
    const { mt4DemoLoginConfig, mt5DemoLoginConfig } = this.context.access;

    return this.isMT5 ? mt5DemoLoginConfig : mt4DemoLoginConfig;
  };

  public getUserServerTimezoneOffset = () => {
    return this.context.company?.serverTimezone;
  };

  public getServerTimezoneGMTString = (wrapInBrackets = false): string => {
    const offset = this.context.company?.serverTimezone;
    if (typeof offset !== 'number') {
      return '';
    }

    const gmtString = offset > 0 ? `GMT+${offset}` : `GMT${offset}`;
    return wrapInBrackets ? `(${gmtString})` : gmtString;
  };

  public getTimezoneDesc = (isUseServerAndTz?: boolean) => {
    if (isUseServerAndTz) {
      const gmtString = this.getServerTimezoneGMTString(true);
      return i18n.t('The date and time follows server’s timezone {{gmtString}}.', { gmtString });
    }

    const isServerTime = this.getMarketTimeIsServer();

    if (isServerTime) {
      return i18n.t('Date and time are based on server timezone.');
    }
    return i18n.t('Date and time are based on device timezone.');
  };

  // Return offset depending on the configured market time management
  public getMarketAdjustedTimezoneOffset = () => {
    const isServerTime = this.getMarketTimeIsServer();
    const serverTimezoneOffset = this.getUserServerTimezoneOffset();
    const deviceTimezoneOffset = dayjs().utcOffset() / 60;

    return isServerTime && serverTimezoneOffset !== undefined ? serverTimezoneOffset : deviceTimezoneOffset;
  };

  // Returns server timeoffset only if in server time
  public getTimezoneOffset = () => {
    const isServerTime = this.getMarketTimeIsServer();
    if (isServerTime) {
      return this.getUserServerTimezoneOffset();
    }
    return undefined;
  };

  public convertToDayjsWithOffset = ({ input, keepTime, baseFromOffset }: IConvertToDayjsWithOffsetParams = {}) => {
    const isServerTime = this.getMarketTimeIsServer();
    const offset = this.getTimezoneOffset();
    return getUTCOffsetDateTimeAsDayjs({
      input,
      utcOffset: offset !== undefined && isServerTime ? offset : undefined,
      keepTime,
      baseFromOffset
    });
  };

  public convertToDayjsWithOffsetServerTime = ({
    input,
    keepTime,
    baseFromOffset
  }: IConvertToDayjsWithOffsetParams = {}) => {
    const utcOffset = this.getUserServerTimezoneOffset();

    return getUTCOffsetDateTimeAsDayjs({
      input,
      utcOffset,
      keepTime,
      baseFromOffset
    });
  };

  public getTenantId = () => {
    return this.context.company?.tenantId;
  };
}

export default new CompanyService();
